import { type CoreFiltersFrontendState } from '@autone/redux/core';
import { type Draft, produce } from 'immer';
import { createContext, useCallback, useContext } from 'react';

type CoreGeneralFiltersContextValue = [
  CoreFiltersFrontendState,
  (update: (draft: Draft<CoreFiltersFrontendState>) => void) => void,
];

export const CoreGeneralFiltersContext =
  createContext<CoreGeneralFiltersContextValue | null>(null);

interface CoreGeneralFiltersContextProviderProps {
  children: React.ReactNode;
  coreFilters: CoreFiltersFrontendState;
  onCoreFiltersChange: (filters: CoreFiltersFrontendState) => void;
}

export const CoreGeneralFiltersContextProvider = ({
  children,
  coreFilters,
  onCoreFiltersChange,
}: CoreGeneralFiltersContextProviderProps) => {
  const setCoreFilters = useCallback(
    (update: (draft: Draft<CoreFiltersFrontendState>) => void) => {
      const newFilters = produce(coreFilters, update);
      onCoreFiltersChange(newFilters);
    },
    [onCoreFiltersChange, coreFilters],
  );

  return (
    <CoreGeneralFiltersContext.Provider value={[coreFilters, setCoreFilters]}>
      {children}
    </CoreGeneralFiltersContext.Provider>
  );
};

export const useCoreGeneralFiltersContext = () => {
  const context = useContext(CoreGeneralFiltersContext);
  if (!context) {
    throw new Error(
      'useCoreGeneralFiltersContext must be used within a CoreGeneralFiltersContextProvider',
    );
  }
  return context;
};
